import {css, StyleSheet} from 'aphrodite';
import React, {useState, KeyboardEvent, ChangeEvent, useRef} from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY, GRAY_DARKER, GRAY_LIGHTER, WHITE} from 'ui-library/Colors';

export interface DeepResearchChatInputControls {
  inputText: string;
  setInputText: (message: string) => void;
}

/**
 * Custom hook for managing related input state.
 */
export function useDeepResearchChatInputControls(): DeepResearchChatInputControls {
  const [inputText, setInputText] = useState<string>('');

  // TODO (BNCH-126623): Add file related state and functionality

  return {
    inputText,
    setInputText,
  };
}

export interface DeepResearchChatInputProps {
  inputText: string;
  setInputText: (message: string) => void;
  onSubmit: () => void;
  inputDisabled: boolean;
}

export function DeepResearchChatInput({
  inputText,
  setInputText,
  onSubmit,
  inputDisabled,
}: DeepResearchChatInputProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setInputText(e.target.value);
    autoResize();
  };

  const autoResize = (): void => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight to fit content
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const isSubmitDisabled = inputDisabled || !inputText.trim();

  return (
    <div className={css(styles.inputContainer)}>
      <div className={css(styles.inputGroup, inputDisabled && styles.inputGroupDisabled)}>
        <textarea
          ref={textareaRef}
          className={css(styles.input)}
          placeholder="Get a detailed report"
          autoFocus={true}
          value={inputText}
          onChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          disabled={inputDisabled}
        />
        <div className={css(styles.inputGroupButtonRow)}>
          {/* TODO: Add file and @ button functionality */}
          <div className={css(styles.inputGroupIconButtons)}>
            {/* <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              onClick={noop}
              icon={<BenchlingIcon icon="paperclip" />}
            />
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              onClick={noop}
              icon={<BenchlingIcon icon="at" />}
            /> */}
          </div>
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.MEDIUM}
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            text="Start Research"
          />
        </div>
      </div>
      <div className={css(styles.warningMessage)}>AI can make mistakes. Always check results.</div>
    </div>
  );
}

const styles = StyleSheet.create({
  inputContainer: {
    padding: '12px',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '4px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    border: `1px solid ${GRAY}`,
    borderRadius: `3px`,
    backgroundColor: WHITE,
    ':focus-within': {
      borderColor: BLUE_LIGHT,
    },
  },
  inputGroupDisabled: {
    backgroundColor: GRAY_LIGHTER,
  },
  input: {
    flexGrow: 1,
    width: '100%',
    minHeight: '80px',
    padding: '12px',
    fontFamily: 'inherit',
    fontSize: '14px',
    border: 'none',
    background: 'transparent',
    resize: 'none',
    overflow: 'hidden',
    ':focus': {
      outline: 'none',
    },
  },
  inputGroupButtonRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 12px',
  },
  inputGroupIconButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  warningMessage: {
    fontSize: '12px',
    color: GRAY_DARKER,
    textAlign: 'center',
  },
});
